import React, { useState, ChangeEvent, FormEvent } from 'react';
import { Mail, Phone, MapPin } from 'lucide-react';
import { ContactFormData, ContactInfo } from '../types';
import { submitContactForm } from '../services/api';
import '../styles/Contact.css';

const Contact: React.FC = () => {
  const contactInfo: ContactInfo[] = [
    { id: 1, icon: Mail, title: 'Email', data: 'hello@datryx.co' },
    { id: 2, icon: Phone, title: 'Phone', data: '+44 7466 152039' },
    { id: 3, icon: MapPin, title: 'Location', data: 'London, United Kingdom' },
  ];

  const [formData, setFormData] = useState<ContactFormData>({
    fullName: '',
    email: '',
    countryCode: '',
    phone: '',
    message: '',
    consent: false,
  });

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleInputChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ): void => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      const target = e.target as HTMLInputElement;
      setFormData((prev) => ({ ...prev, [name]: target.checked }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await submitContactForm(formData);
      setSubmitStatus('success');
      setFormData({ fullName: '', email: '', countryCode: '', phone: '', message: '', consent: false });
    } catch (error) {
      console.error('Submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="contact section-padding">
      <div className="container">
        <div className="contact-layout">
          <div className="contact-intro reveal">
            <span className="eyebrow">Get in touch</span>
            <h2>Let&rsquo;s automate your hardest workflow.</h2>
            <p>
              Tell us about the process eating your team&rsquo;s week. We&rsquo;ll reply within
              a day with either a clear plan—or an honest &ldquo;we&rsquo;re not the right fit.&rdquo;
            </p>

            <ul className="contact-info-list">
              {contactInfo.map((info: ContactInfo) => {
                const Icon = info.icon;
                return (
                  <li key={info.id} className="info-row">
                    <div className="info-icon">
                      <Icon size={16} />
                    </div>
                    <div>
                      <div className="info-title">{info.title}</div>
                      <div className="info-data">{info.data}</div>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>

          <form className="contact-form reveal" onSubmit={handleSubmit}>
            <div className="form-row">
              <div className="form-field">
                <label htmlFor="fullName">Full name</label>
                <input
                  type="text"
                  id="fullName"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleInputChange}
                  placeholder="Jane Doe"
                  required
                />
              </div>
              <div className="form-field">
                <label htmlFor="email">Work email</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="jane@company.com"
                  required
                />
              </div>
            </div>

            <div className="form-field">
              <label htmlFor="phone">Phone (optional)</label>
              <div className="phone-input-group">
                <input
                  type="text"
                  id="countryCode"
                  name="countryCode"
                  value={formData.countryCode || ''}
                  onChange={handleInputChange}
                  placeholder="+44"
                  className="country-code-select"
                />
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone || ''}
                  onChange={handleInputChange}
                  placeholder="7466 152039"
                  className="phone-number-input"
                />
              </div>
            </div>

            <div className="form-field">
              <label htmlFor="message">What are you trying to automate?</label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                rows={5}
                placeholder="A few sentences on the workflow, the team it affects, and what good looks like..."
                required
              />
            </div>

            <div className="form-checkbox">
              <input
                type="checkbox"
                id="consent"
                name="consent"
                checked={formData.consent}
                onChange={handleInputChange}
                required
              />
              <label htmlFor="consent">
                I agree to Datryx contacting me about my enquiry.
              </label>
            </div>

            <button type="submit" className="btn-gradient" disabled={isSubmitting}>
              {isSubmitting ? 'Sending…' : 'Send message'}
            </button>

            {submitStatus === 'success' && (
              <div className="success-message">Thanks—we&rsquo;ll be in touch within one business day.</div>
            )}
            {submitStatus === 'error' && (
              <div className="error-message">Couldn&rsquo;t send. Please email hello@datryx.co instead.</div>
            )}
          </form>
        </div>
      </div>
    </section>
  );
};

export default Contact;
